import { useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import styled, { css } from 'styled-components';
import { HiOutlineUser } from 'react-icons/hi2';

import theme from '@styles/theme';
import AlertIcon from '@assets/icon/alertIcon.svg';
import { headerTabMenu } from '@lib/data';
import { tabMenuType } from '@utils/types';
import { userVar } from '@store/index';

import MyProfileModal from '@components/share/Modal/MyProfileModal';
import ChangePasswordModal from '@components/share/Modal/ChangePasswordModal';

const Header = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { master, pwUpdateDate, passwordInit } = useReactiveVar(userVar);
    const [openProfile, setOpenProfile] = useState(false);
    const [passwordModal, setPasswordModal] = useState(false);

    const activePathname = useCallback(
        (type: string) => {
            switch (type) {
                case '대시보드':
                    return pathname === '/dashboard';
                case '회원':
                    return pathname.split('/')[1] === 'member';
                case '상품':
                    return pathname.split('/')[1] === 'product';
                case '운영':
                    return pathname.split('/')[1] === 'operation';
            }
        },
        [pathname]
    );
    // FullFormPage 사용 시, 기존 헤더 숨김
    const hideHeader = pathname.includes('/new');

    // 비밀번호 변경 기간 체크 (6개월)
    useEffect(() => {
        const lastUpdate = new Date(pwUpdateDate as string);
        const diffMilliseconds = Date.now() - lastUpdate.getTime();
        const diffDate = Math.floor(
            Math.abs(diffMilliseconds / (1000 * 60 * 60 * 24))
        );

        // 비밀번호 초기화를 했거나 변경 6개월이 넘었을 경우, 로그인 직후에만 모달 오픈
        if (passwordInit || diffDate > 180) {
            setPasswordModal(true);
        }
    }, [pwUpdateDate, passwordInit]);

    if (hideHeader) return null;

    return (
        <Wrapper>
            <Contents>
                <Nav>
                    <LogoTitle onClick={() => navigate('/')}>
                        IT'S PET ADMIN
                    </LogoTitle>
                    <MenuList>
                        {headerTabMenu.map(
                            (list: tabMenuType, index: number) => (
                                <MenuItem key={`${index}-tab-${list.dataText}`}>
                                    <MenuText
                                        to={`${list.pathname}`}
                                        $active={activePathname(list.dataText)}
                                        data-text={list.dataText}
                                    >
                                        {list.dataText}
                                    </MenuText>
                                </MenuItem>
                            )
                        )}
                    </MenuList>
                </Nav>
                <Nav>
                    {master ? ( // 마스터 관리자 여부
                        <>
                            <MasterText
                                to="/setting/sysop"
                                $active={pathname.includes('/setting')}
                                data-text="설정"
                            >
                                설정
                            </MasterText>
                            <Button>
                                <Image src={AlertIcon} alt="알림" />
                            </Button>
                            <MasterButton onClick={() => setOpenProfile(true)}>
                                M
                            </MasterButton>
                        </>
                    ) : (
                        <>
                            <Button>
                                <Image src={AlertIcon} alt="알림" />
                            </Button>
                            <Button
                                onClick={() => setOpenProfile(true)}
                                $margin="0 0 0 10px"
                            >
                                <HiOutlineUser fontSize={20} />
                            </Button>
                        </>
                    )}
                </Nav>
            </Contents>
            {openProfile && <MyProfileModal setOpenProfile={setOpenProfile} />}
            {passwordModal && (
                <ChangePasswordModal
                    firstLogin={passwordInit as boolean}
                    changePw={passwordModal}
                    setChangePw={setPasswordModal}
                />
            )}
        </Wrapper>
    );
};

export default Header;

const Wrapper = styled.header`
    position: sticky;
    top: 0;
    width: 100%;
    height: 80px;
    border-bottom: 1px solid ${theme.colors.ultraLightGrayBorder};
    background-color: ${theme.colors.whiteColor};
    z-index: 5;
`;

const Contents = styled.nav`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 auto;
    width: 1280px;
    height: 80px;
`;

const Nav = styled.div`
    display: flex;
    align-items: center;
`;

const LogoTitle = styled.h1`
    margin-right: 54px;
    font-size: 16px;
    font-family: PretendardMedium, sans-serif;
    cursor: pointer;
`;
const MenuList = styled.ul`
    display: flex;
    align-items: center;
    gap: 20px;
    font-size: 14px;
`;
const MenuItem = styled.li`
    line-height: 1;
    cursor: pointer;
`;
const MenuText = styled(NavLink)<{ $active?: boolean }>`
    position: relative;
    color: ${theme.colors.whiteColor};

    &::before {
        content: attr(data-text);
        position: absolute;
        top: 0;
        left: 0;
        opacity: 1;
        color: ${theme.colors.blackColor};
        transition: 0.25s;
    }

    &::after {
        content: attr(data-text);
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
        color: ${theme.colors.blackColor};
        font-family: PretendardSemiBold, sans-serif;
        transition: 0.25s;
    }

    &:hover {
        &::before {
            opacity: 0;
        }
        &::after {
            opacity: 1;
        }
    }

    ${({ $active }) =>
        $active &&
        css`
            &::before {
                opacity: 0;
            }
            &::after {
                opacity: 1;
            }
        `};
`;
const MasterText = styled(MenuText)`
    margin-right: 20px;
    font-size: 14px;

    &:hover {
        font-family: PretendardSemiBold, sans-serif;
    }

    ${({ $active }) =>
        $active &&
        css`
            font-family: PretendardSemiBold, sans-serif;
        `};
`;
const MasterButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 10px;
    width: 32px;
    height: 32px;
    font-size: 18px;
    color: ${theme.colors.pointGreen};
    border: ${theme.colors.lightGrayBorder} 1px solid;
    border-radius: 4px;
    font-family: PretendardMedium, sans-serif;
    transition: 0.25s;
    &:hover {
        background-color: ${theme.colors.lightGrayHoverColor};
    }
`;
const Button = styled.button<{ $margin?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
    width: 32px;
    height: 32px;
    color: ${theme.colors.grayIconColor};
    border: ${theme.colors.lightGrayBorder} 1px solid;
    border-radius: 4px;
    transition: 0.25s;

    &:hover {
        background-color: ${theme.colors.lightGrayHoverColor};
    }
`;
const Image = styled.img`
    display: block;
    width: 16px;
    height: 16px;
`;
